import { BehaviorSubject, ignoreElements, Observable, skip, switchMap, tap, takeUntil } from "rxjs";
import { Sort } from "@angular/material/sort";
import { PageEvent } from "@angular/material/paginator";
import { Page, TableDataSource } from "@sxm-content-creators/tools-ds";
import { SubmissionFreeformApiService } from "../../../services/submission-freeform.api.service";
import { SubmissionFreeformResponse, SubmissionFreeformSearchRequest } from "../../../models/submission-freeform.model";

const DEFAULT_PAGE_SIZE = 50;
const DEFAULT_SORT: Sort = { active: "Date", direction: "desc" };

export class FreeformDashboardDataSource implements TableDataSource<SubmissionFreeformResponse> {
    readonly page: BehaviorSubject<Page<SubmissionFreeformResponse>>;
    private readonly _data = new BehaviorSubject<SubmissionFreeformResponse[]>([]);
    private readonly _count = new BehaviorSubject<number>(0);

    size = DEFAULT_PAGE_SIZE;

    constructor(
        private readonly api: SubmissionFreeformApiService,
        private readonly buildRequest: (page: Page<SubmissionFreeformResponse>) => SubmissionFreeformSearchRequest,
        filterChange$: Observable<unknown>,
        defaultSort: Sort = DEFAULT_SORT
    ) {
        this.page = new BehaviorSubject<Page<SubmissionFreeformResponse>>({
            index: 0,
            size: this.size,
            sort: defaultSort,
            filter: ""
        });

        const destroy$ = this.page.pipe(ignoreElements());

        filterChange$.pipe(takeUntil(destroy$)).subscribe(() => this.refreshPage());

        this.page
            .pipe(
                // skip(1): component is constructed before NavigationEnd, so selectedInteractiveIds
                // still holds the previous route's ID at this point. ngOnInit calls refreshPage()
                // after NavigationEnd has updated the IDs.
                skip(1),
                switchMap(page =>
                    this.api.searchSubmissions(this.buildRequest(page)).pipe(
                        tap(response => {
                            this._data.next(response.items);
                            this._count.next(response.totalItems);
                        })
                    )
                ),
                takeUntil(destroy$)
            )
            .subscribe();
    }

    get data(): SubmissionFreeformResponse[] {
        return this._data.value;
    }

    get renderedData(): SubmissionFreeformResponse[] {
        return this._data.value;
    }

    readonly count$ = this._count;

    count(): Observable<number> {
        return this._count;
    }

    filterChange(_filter: string): void {
        // filtering is managed by {@link FreeformFilterService}; pass its signal as an observable via the constructor
    }

    pageChange({ pageIndex, pageSize }: PageEvent): void {
        this.size = pageSize;
        this.page.next({ ...this.page.value, index: pageIndex, size: pageSize });
    }

    sortChange(sort: Sort): void {
        // reset the page when changing sort
        this.page.next({ ...this.page.value, index: 0, sort });
    }

    refreshPage(): void {
        this.page.next({ ...this.page.value, index: 0 });
    }

    connect(): Observable<SubmissionFreeformResponse[]> {
        return this._data.asObservable();
    }

    disconnect(): void {
        this.page.complete();
        this._data.complete();
        this._count.complete();
    }
}
